import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import BotCard from './components/BotCard';
import ConfigEditor from './components/ConfigEditor';
import LogViewer from './components/LogViewer';
import LoginForm from './components/LoginForm';
import Header from './components/Header';
import { useAuth, ProtectedRoute } from './contexts/AuthContext';
import { botApi } from './services/api';
// import { socketService } from './services/socket';
// import { BotConfig } from './types';

const App: React.FC = () => {
  const { user, login, error, isLoading: authLoading } = useAuth();
  const [selectedBot, setSelectedBot] = useState<string | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  // Query for getting bots
  const { data: bots = [], isLoading, refetch } = useQuery({
    queryKey: ['bots'],
    queryFn: botApi.getBots,
    enabled: !!user, // Sadece giriş yapılmışsa çalıştır
    // refetchInterval: 5000, // Otomatik yenileme deaktif - sadece manuel yenileme
  });

  // Mutations for bot control
  const startBotMutation = useMutation({
    mutationFn: botApi.startBot,
    onSuccess: (_, botName) => {
      toast.success(`${botName} başarıyla başlatıldı`);
      queryClient.invalidateQueries({ queryKey: ['bots'] });
    },
    onError: (error: any) => {
      toast.error(`Bot başlatılamadı: ${error.message}`);
    },
  });

  const stopBotMutation = useMutation({
    mutationFn: botApi.stopBot,
    onSuccess: (_, botName) => {
      toast.success(`${botName} başarıyla durduruldu`);
      queryClient.invalidateQueries({ queryKey: ['bots'] });
    },
    onError: (error: any) => {
      toast.error(`Bot durdurulamadı: ${error.message}`);
    },
  });

  const restartBotMutation = useMutation({
    mutationFn: botApi.restartBot,
    onSuccess: (_, botName) => {
      toast.success(`${botName} başarıyla yeniden başlatıldı`);
      queryClient.invalidateQueries({ queryKey: ['bots'] });
    },
    onError: (error: any) => {
      toast.error(`Bot yeniden başlatılamadı: ${error.message}`);
    },
  });

  // Socket connection for real-time updates
  useEffect(() => {
    // socketService.connect();

    // socketService.subscribeToBotStatus((updatedBots: BotConfig[]) => {
    //   queryClient.setQueryData(['bots'], updatedBots);
    // });

    // return () => {
    //   socketService.unsubscribeFromBotStatus();
    //   socketService.disconnect();
    // };
  }, [queryClient]);

  const handleStartBot = (botName: string) => {
    startBotMutation.mutate(botName);
  };

  const handleStopBot = (botName: string) => {
    stopBotMutation.mutate(botName);
  };

  const handleRestartBot = (botName: string) => {
    restartBotMutation.mutate(botName);
  };

  const handleOpenConfig = (botName: string) => {
    setSelectedBot(botName);
    setConfigDialogOpen(true);
  };

  const handleViewLogs = (botName: string) => {
    setSelectedBot(botName);
    setLogsDialogOpen(true);
  };

  const handleRefresh = () => {
    refetch();
    toast.success('Bot durumları güncellendi');
  };

  const handleLogin = async (username: string, password: string): Promise<void> => {
    const success = await login(username, password);
    if (success) {
      toast.success(`Hoş geldiniz, ${username}! 🎉`);
    }
  };

  const isAnyMutationLoading = 
    startBotMutation.isPending || 
    stopBotMutation.isPending || 
    restartBotMutation.isPending;

  // Giriş yapılmamışsa login formunu göster
  if (!user) {
    return (
      <LoginForm 
        onLogin={handleLogin}
        error={error || undefined}
        loading={authLoading}
      />
    );
  }

  // Giriş yapılmışsa ana paneli göster
  return (
    <ProtectedRoute>
      <Box sx={{ 
        flexGrow: 1, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh'
      }}>
        <Header 
          onRefresh={handleRefresh}
          isLoading={isLoading}
        />

      <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
        <Box sx={{ 
          textAlign: 'center',
          mb: 4,
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '20px',
          p: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          <Typography 
            variant="h3" 
            gutterBottom
            sx={{
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
              mb: 1
            }}
          >
            📊 Bot Dashboard
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Telegram Bot'larınızı yönetin ve izleyin
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {bots.map((bot) => (
            <Grid item xs={12} sm={6} md={4} key={bot.name}>
              <BotCard
                bot={bot}
                onStart={handleStartBot}
                onStop={handleStopBot}
                onRestart={handleRestartBot}
                onOpenConfig={handleOpenConfig}
                onViewLogs={handleViewLogs}
                loading={isAnyMutationLoading}
              />
            </Grid>
          ))}
        </Grid>

        {bots.length === 0 && !isLoading && (
          <Box 
            display="flex" 
            justifyContent="center" 
            mt={4}
            sx={{
              background: 'rgba(255,255,255,0.9)',
              borderRadius: '15px',
              p: 4,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}
          >
            <Typography variant="h6" color="text.secondary">
              🔍 Bot bulunamadı
            </Typography>
          </Box>
        )}
      </Container>

      {/* Config Dialog */}
      <ConfigEditor
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        botName={selectedBot || ''}
      />

      {/* Logs Dialog */}
      <LogViewer
        open={logsDialogOpen}
        onClose={() => setLogsDialogOpen(false)}
        botName={selectedBot || ''}
      />
      </Box>
    </ProtectedRoute>
  );
};

export default App;
